import { ArrowDownward } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  ICompanyFormation,
  ICompanyFormationFullObject,
} from '../../Interfaces/ICompanyFormation';
import { IInheritFullObject } from '../../Interfaces/IInherit';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalTransactionDataCollection } from '../../Interfaces/ILegalTransaction';
import { ILegalTransactionRelationship } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { LegalTransactionRelationshipTypeEnum } from '../../Interfaces/ILegalTransactionRelationship/LegalTransactionRelationshipTypeEnum';
import { IMortgage, IMortgageFullObject } from '../../Interfaces/IMortgage';
import { CompanyFormationMain } from '../CompanyFormation/CompanyFormationMain';
import { InheritMain } from '../Inherit/InheritMain';
import { MortgageMain } from '../Mortgage/MortgageMain';
import { PurchaseContractMain } from '../PurchaseContract/PurchaseContractMain';
import { AddLegalTransactionRelationshipDialog } from './AddLegalTransactionRelationshipDialog';

const DataCollectionAccordion: React.FC<{
  title: string;
  children: React.ReactElement;
}> = ({ title, children }) => (
  <Accordion>
    <AccordionSummary expandIcon={<ArrowDownward />}>
      <Typography>{title}</Typography>
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

const isCompanyFormation = (
  dataCollection: ILegalTransactionDataCollection
): dataCollection is ICompanyFormationFullObject => {
  return 'idCompanyFormation' in dataCollection;
};

const isInherit = (
  dataCollection: ILegalTransactionDataCollection
): dataCollection is IInheritFullObject => {
  return 'idInherit' in dataCollection;
};

const isMortgage = (
  dataCollection: ILegalTransactionDataCollection
): dataCollection is IMortgageFullObject => {
  return 'idMortgage' in dataCollection;
};

const dataCollectionTitle = (
  dataCollection: ILegalTransactionDataCollection
) => {
  if (isCompanyFormation(dataCollection)) {
    return companyFormationTitle(dataCollection);
  } else if (isInherit(dataCollection)) {
    return dataCollection.PersonArray.length > 0
      ? `Erbscheinsantrag - ${dataCollection.PersonArray[0].FirstName} ${dataCollection.PersonArray[0].LastName}`
      : 'Erbscheinsantrag';
  } else if (isMortgage(dataCollection)) {
    return dataCollection.RealEstate.Street &&
      dataCollection.RealEstate.StreetNr
      ? `Grundschuld - ${dataCollection.RealEstate.Street} ${dataCollection.RealEstate.StreetNr}`
      : 'Grundschuld';
  }
  return '';
};

const issueTitle = (relationship: ILegalTransactionRelationship) => {
  if (
    relationship.idRelationshipType ===
    LegalTransactionRelationshipTypeEnum.PurchaseContract
  ) {
    if (relationship.RealEstateArray.length === 0) return 'Kaufvertrag';
    const { Street, StreetNr, Longitude, Latitude } =
      relationship.RealEstateArray[0];
    return `Kaufvertrag - ${Street || Longitude} ${StreetNr || Latitude}`;
  }
  return '';
};

const companyFormationTitle = (companyFormation: ICompanyFormation) => {
  let prefix = 'Unternehmens';
  let sufix = 'änderung';

  if (companyFormation.idBusinessForm === 12) {
    prefix = 'Vereins';
  }
  if (companyFormation.isFounding) {
    sufix = 'gründung';
  }
  return companyFormation.CompanyName
    ? `${prefix}${sufix} - ${companyFormation.CompanyName}`
    : `${prefix}${sufix}`;
};

interface IProps {
  relationships: ILegalTransactionRelationship[];
  setRelationships: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
  dataCollection: ILegalTransactionDataCollection[];
  setDataCollection: React.Dispatch<
    React.SetStateAction<ILegalTransactionDataCollection[]>
  >;
  takenLegalClients: ILegalClient[];
  setTakenLegalClients: React.Dispatch<React.SetStateAction<ILegalClient[]>>;
  isSavePossible: (boolean | Record<string, boolean>)[];
  setIsSavePossible: React.Dispatch<
    React.SetStateAction<(boolean | Record<string, boolean>)[]>
  >;
}

export const LegalTransactionDataCollection: React.FC<IProps> = props => {
  const {
    relationships,
    setRelationships,
    dataCollection,
    setDataCollection,
    takenLegalClients,
    setTakenLegalClients,
    isSavePossible,
    setIsSavePossible,
  } = props;
  const [isMount, setIsMount] = useState(false);

  const changeArray = (updatedObject: any, idx: number) => {
    const tmpArray = [...dataCollection];
    tmpArray[idx] = updatedObject;
    setDataCollection(tmpArray);
  };

  const changeIsSavePossible = (updatedObject: any, idx: number) => {
    const tempArray = [...isSavePossible];
    tempArray[idx] = updatedObject;
    setIsSavePossible(tempArray);
  };

  const setRelationship = (newRelationship: ILegalTransactionRelationship) =>
    setRelationships(x =>
      x.map(r =>
        r.idRelationship === newRelationship.idRelationship
          ? newRelationship
          : r
      )
    );

  return (
    <>
      <Typography variant='h5' sx={{ mb: 3 }}>
        Datenerfassung
        <Button
          sx={{ float: 'right' }}
          variant='outlined'
          onClick={() => setIsMount(true)}
        >
          Hinzufügen
        </Button>
      </Typography>

      {!dataCollection.length && !relationships.length && (
        <>
          <i>Keine Datenerfassung vorhanden </i>
        </>
      )}

      {relationships.map((r, i) => {
        if (
          r.idRelationshipType ===
          LegalTransactionRelationshipTypeEnum.PurchaseContract
        ) {
          return (
            <DataCollectionAccordion title={issueTitle(r)} key={i}>
              <PurchaseContractMain
                key={i}
                relationship={r}
                setRelationship={setRelationship}
                takenLegalClients={takenLegalClients}
                setTakenLegalClients={setTakenLegalClients}
              />
            </DataCollectionAccordion>
          );
        }
      })}

      {dataCollection.map((x, i) => {
        if ('idCompanyFormation' in x) {
          return (
            <DataCollectionAccordion title={dataCollectionTitle(x)} key={i}>
              <CompanyFormationMain
                key={`datacollection-element-${i}`}
                companyFormationObject={x}
                setCompanyFormationObject={(updatedObject: ICompanyFormation) =>
                  changeArray(updatedObject, i)
                }
              />
            </DataCollectionAccordion>
          );
        } else if ('idInherit' in x) {
          return (
            <DataCollectionAccordion title={dataCollectionTitle(x)} key={i}>
              <InheritMain
                inheritObject={x}
                setInheritObject={(updatedObject: IInheritFullObject) =>
                  changeArray(updatedObject, i)
                }
              />
            </DataCollectionAccordion>
          );
        } else if ('idMortgage' in x) {
          return (
            <DataCollectionAccordion title={dataCollectionTitle(x)} key={i}>
              <MortgageMain
                isSavePossibleValue={
                  isSavePossible[i] as Record<string, boolean>
                }
                setIsSavePossible={(updatedObject: any) =>
                  changeIsSavePossible(updatedObject, i)
                }
                mortgageObject={x}
                setMortgageObject={(updatedObject: IMortgage) =>
                  changeArray(updatedObject, i)
                }
              />
            </DataCollectionAccordion>
          );
        }
      })}

      {isMount && (
        <AddLegalTransactionRelationshipDialog
          {...props}
          unmount={() => setIsMount(false)}
        />
      )}
    </>
  );
};
